import React, { useEffect, useCallback, useMemo, memo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Button, Spinner } from '@iq/react-components';

import { DEFAULT_AGGREGATION_OPTIONS } from '../../constants';
import { getStaticComponents } from '../../bundles/components';
import {
  getVisualizations,
  upsertVisualization,
  getSingleVisualization,
  saveVisualization,
  LAST_VALUE_VISUALIZATIONS,
} from '../../bundles/visualizations';
import {
  getSources,
  getSourceTypes,
  getSiteVariables,
  getSourcesLoaded,
} from '../../bundles/sources';
import { getStateSets } from '../../bundles/statesets';
import {
  useDateSubscription,
  getRangeFromLabel,
  getRangeFromPartial,
} from '../../bundles/application';
import { units, getValidUnitsForType } from '../../units';
import { useDebounce } from '../../utils';
import colors from '../../colors';
import { getTags, requestTags } from '../../bundles/tags';
import config from '../../config';
import { getHasPermission } from '../../bundles/auth';

import Tabs, { Tab } from '../Tabs/Tabs';
import Visualization, {
  schema,
  uiSchema,
  visualizations as visualizationTypes,
  signalCounts,
  getRequiredStarSignalCount,
  StarSignalCountMessage,
} from '../Visualization';
import JsonEditor from '../JsonEditor';
import BigModal from '../BigModal';
import Heading from '../Heading';
import InlineEditor from '../InlineEditor';
import CustomSelect from '../CustomSelect';
import ConfirmationDialog from '../ConfirmationDialog';
import TagSelect from '../TagSelect';
import MinItemsArrayFieldTemplate from './MinItemsArrayFieldTemplate';
import MinItemsArrayFieldItemTemplate from './MinItemsArrayFieldItemTemplate';

import {
  getVariableSchema,
  updateVisualizationSchema,
  getPrioritySignalIds,
  normalizeVisualizationConfigurationColors,
} from './functions';

// bar-gauge supports when in single signal mode
const VISUALIZATIONS_SUPPORTING_STRINGS = new Set(['metric', 'bar-gauge']);
const NUMERIC_DATA_TYPES = new Set(['float', 'integer', 'double', 'number']);
const argumentBasedSchemas = new Set([
  'graph',
  'star',
  'gauge',
  'bar-gauge',
  'status',
  'grouped-bar',
  'duval',
  'duvalPentagon',
]);
const DEFAULT_VISUALIZATION_UNIT_ID = units[0].id;

const normalizeVisualizationAxisUnits = (axes, unitIdsAllowedForType) => {
  const normalizeAxisUnit = (axis) =>
    axis?.unit && !unitIdsAllowedForType.has(axis.unit)
      ? { ...axis, unit: DEFAULT_VISUALIZATION_UNIT_ID }
      : axis;
  const normalizedYLeft = normalizeAxisUnit(axes.yLeft);
  const normalizedYRight = normalizeAxisUnit(axes.yRight);

  if (normalizedYLeft === axes.yLeft && normalizedYRight === axes.yRight) return axes;

  return {
    ...axes,
    ...(normalizedYLeft !== axes.yLeft ? { yLeft: normalizedYLeft } : {}),
    ...(normalizedYRight !== axes.yRight ? { yRight: normalizedYRight } : {}),
  };
};

const normalizeVisualizationVariableUnits = (visualizationVariables, unitIdsAllowedForType) => {
  const hasVariableUnitNotAllowed = visualizationVariables.some(
    (visualizationVariable) =>
      visualizationVariable.unit && !unitIdsAllowedForType.has(visualizationVariable.unit)
  );
  if (!hasVariableUnitNotAllowed) return;

  return visualizationVariables.map((visualizationVariable) =>
    visualizationVariable.unit && !unitIdsAllowedForType.has(visualizationVariable.unit)
      ? { ...visualizationVariable, unit: DEFAULT_VISUALIZATION_UNIT_ID }
      : visualizationVariable
  );
};

const VisualizationEditor = memo(
  ({ onClose, visualizationId, visualizationNames, parentPanelId, componentScope }) => {
    const { id: siteId } = useParams();
    const dispatch = useDispatch();
    const tags = useSelector(getTags);
    const staticComponents = useSelector(getStaticComponents);
    const visualizations = useSelector(getVisualizations);
    const visualization = visualizations.find((v) => v.id === visualizationId);
    const sources = useSelector(getSources);
    const sourcesLoaded = useSelector(getSourcesLoaded);
    const sourceTypes = useSelector(getSourceTypes);
    const siteVariables = useSelector(getSiteVariables);
    const stateSets = useSelector(getStateSets);
    const [isTemplate, setIsTemplate] = useState(!!visualization?.isTemplate);
    const [enableSignalList, setEnableSignalList] = useState(
      !!visualization?.configuration?.singleSignal
    );
    const [selectedSignal, setSelectedSignal] = useState(
      visualization?.configuration?.singleSignalId || null
    );
    const [vizTags, setVizTags] = useState(visualization?.tags || []);
    const [variableSchema, setVariableSchema] = useState(null);
    const [valid, setValid] = useState(false);
    const [initialRelations] = useState(visualization?.relations || []);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [previewSingleSignalTypeError, setPreviewSingleSignalTypeError] = useState(null);

    useEffect(() => {
      dispatch(requestTags(siteId));
    }, [siteId, dispatch, tags]);
    const panelId = 'visualizationEditor';
    const pollingDateRange = useDateSubscription(panelId);

    useEffect(() => {
      if (visualization && !LAST_VALUE_VISUALIZATIONS.has(visualization.type)) {
        dispatch(getSingleVisualization(visualization, panelId));
      }
    }, [dispatch, visualization]);

    const sortedComponents = useMemo(() => {
      const sorted = staticComponents.toSorted((a, b) =>
        a.itemDesignation.localeCompare(b.itemDesignation)
      );
      if (
        componentScope?.activeComponentTypeFilter &&
        componentScope.activeComponentTypeFilter !== 'any'
      ) {
        return sorted.filter(
          (component) => component.type === componentScope.activeComponentTypeFilter
        );
      }
      return sorted;
    }, [staticComponents, componentScope]);

    const sortedSiteVariables = useMemo(
      () =>
        siteVariables.toSorted((a, b) => {
          const { source_id: idA, name: nameA, itemDesignation: desA } = a;
          const { source_id: idB, name: nameB, itemDesignation: desB } = b;
          const sourceA = sources.find((s) => s.id === idA)?.name || '';
          const sourceB = sources.find((s) => s.id === idB)?.name || '';
          return (sourceB + desB + nameB).localeCompare(sourceA + desA + nameA);
        }),
      [siteVariables, sources]
    );

    const canCreateTags = useSelector((state) =>
      getHasPermission(state, 'tags/Write', { org: visualization?.org, site: visualization?.site })
    );

    useEffect(() => {
      const requiredSignalCount =
        visualization.type === 'star'
          ? getRequiredStarSignalCount(visualization.configuration?.inputMode)
          : signalCounts(visualization.type).minSignal;
      setValid(visualization.variables.length >= requiredSignalCount || isTemplate);
    }, [visualization, isTemplate]);

    useEffect(() => {
      if (visualization.type !== 'bar-gauge' || !enableSignalList) {
        setPreviewSingleSignalTypeError(null);
      }
    }, [visualization.type, enableSignalList]);

    /* When variable form is updated or changed. */
    const debouncedGetSingleVisualizationPreview = useDebounce((updatedVisualization) => {
      if (!LAST_VALUE_VISUALIZATIONS.has(updatedVisualization?.type)) {
        dispatch(getSingleVisualization(updatedVisualization, panelId));
      }
    }, 300);

    const onVariableForm = useCallback(
      ({ formData: signals }) => {
        setValid(false);
        dispatch(upsertVisualization(siteId, { id: visualization.id, variables: signals }));

        debouncedGetSingleVisualizationPreview({
          ...visualization,
          variables: signals,
        });
      },
      [siteId, visualization, dispatch, debouncedGetSingleVisualizationPreview]
    );

    const onComponentForm = useCallback(
      (data, refresh = false) => {
        let { formData: relations } = data;

        const scopedComponentIds = new Set(sortedComponents.map((c) => c.id));
        const outOfScopeRelations = initialRelations.filter(
          (r) => r.entity_id && !scopedComponentIds.has(r.entity_id)
        );

        relations = relations.map((r) => ({ ...r, entity_type: 'components' }));
        const mergedRelations = [...outOfScopeRelations, ...relations];
        dispatch(
          upsertVisualization(siteId, {
            id: visualization.id,
            relations: mergedRelations,
          })
        );

        if (refresh && !LAST_VALUE_VISUALIZATIONS.has(visualization?.type)) {
          const relativeAdjustedRange = pollingDateRange?.label
            ? getRangeFromLabel(pollingDateRange.label)
            : getRangeFromPartial(pollingDateRange);
          dispatch(getSingleVisualization(visualization, panelId, relativeAdjustedRange));
        }
      },
      [siteId, sortedComponents, initialRelations, pollingDateRange, visualization, dispatch]
    );

    const typedSchemaAndVariables = useMemo(() => {
      const buildTypedSchema = schema(visualization.type);
      let baseSchema;
      let enrichedVariables = visualization.variables || [];

      if (argumentBasedSchemas.has(visualization.type)) {
        // Build variables with stateset info for threshold schema
        enrichedVariables = (visualization.variables || [])
          .map((variable) => {
            const siteVariable = siteVariables.find((sv) => sv.id === variable.id);
            if (!siteVariable) return null;
            // siteVariable.stateset can be stale, use the live one from the statesets store if we have it
            const embeddedStateset = siteVariable?.stateset || variable.stateset;
            const liveStateset =
              embeddedStateset && stateSets.find((ss) => ss.id === embeddedStateset.id);
            return {
              ...variable,
              name: siteVariable?.name,
              label: variable.label || siteVariable?.name,
              stateset: liveStateset || embeddedStateset,
              sourceName: sources.find((s) => s.id === siteVariable?.source_id)?.name || '-',
              itemDesignation: siteVariable?.itemDesignation || '-',
            };
          })
          .filter(Boolean);
        baseSchema = buildTypedSchema(enrichedVariables);
      } else {
        baseSchema = buildTypedSchema();
      }

      // Clone the schema to avoid mutating the original shared object
      const clonedSchema = {
        ...baseSchema,
        properties: { ...baseSchema.properties },
      };

      const variablesWithDefinedAgg = enrichedVariables
        .filter((variable) => variable.aggregate)
        .map((variable) => ({
          ...variable,
          seriesLabel: `[ ${variable.itemDesignation || '-'} ] ${variable.label || variable.name} (${variable.aggregate})`,
        }));

      return [clonedSchema, variablesWithDefinedAgg];
    }, [visualization.variables, visualization.type, siteVariables, stateSets, sources]);

    const updatedSchema = useMemo(() => {
      const [typedSchema, variablesWithDefinedAgg] = typedSchemaAndVariables;
      return updateVisualizationSchema({
        visualization,
        variables: variablesWithDefinedAgg,
        schema: typedSchema,
        colors,
      });
    }, [visualization, typedSchemaAndVariables]);

    const siteVariablesById = useMemo(
      () => Object.fromEntries(siteVariables.map((variable) => [variable.id, variable])),
      [siteVariables]
    );

    const updatedUiSchema = useMemo(() => {
      const base = uiSchema(visualization.type) || {};
      let result = base;

      if (visualization.type === 'star' && visualization.configuration?.inputMode === 'cartesian') {
        result = {
          ...result,
          rollingAverage: {
            ...result.rollingAverage,
            'ui:disabled': true,
          },
        };
      }

      if (
        ['star', 'duval', 'duvalPentagon'].includes(visualization.type) &&
        !visualization.configuration?.rollingAverage?.show
      ) {
        result = {
          ...result,
          rollingAverage: {
            ...result.rollingAverage,
            decay: {
              ...result.rollingAverage?.decay,
              'ui:widget': 'hidden',
            },
          },
        };
      }

      return result;
    }, [
      visualization.type,
      visualization.configuration?.inputMode,
      visualization.configuration?.rollingAverage?.show,
    ]);

    const typedSources = useMemo(
      () =>
        sources.map((source) => {
          const sType = sourceTypes.find((t) => t.name === source.type);
          if (sType) {
            const { aggregationOptions, aggregationOptionNames, aggregationDefault } = sType;
            return {
              ...source,
              aggregationOptions,
              aggregationOptionNames,
              aggregationDefault,
            };
          }
          return source;
        }),
      [sources, sourceTypes]
    );

    const variableSources = useMemo(
      () =>
        sortedSiteVariables.reduce(
          (acc, v) => {
            const vSourceIndex = acc.findIndex((source) => source.id === v.source_id);
            if (vSourceIndex !== -1) {
              acc[vSourceIndex].variables = [...(acc[vSourceIndex].variables || []), v.id];
            }
            return acc;
          },
          [...typedSources]
        ),
      [typedSources, sortedSiteVariables]
    );

    const { type: vizType } = visualization;
    const currentSignalCounts = useMemo(() => signalCounts(vizType), [vizType]);

    // Star's real minimum depends on inputMode (vectors=3, cartesian=2), unlike the generic
    // per-type minSignal used for every other visualization.
    const requiredMinSignal = useMemo(
      () =>
        vizType === 'star'
          ? getRequiredStarSignalCount(visualization.configuration?.inputMode)
          : currentSignalCounts.minSignal,
      [vizType, currentSignalCounts, visualization.configuration?.inputMode]
    );

    const previewInfoMarginTop = useMemo(() => {
      if (vizType === 'star' && visualization.configuration?.legend?.show) {
        const thresholds = visualization.configuration?.thresholds;
        const thresholdType = thresholds?.thresholdType;
        const thresholdCount =
          thresholdType === 'linear'
            ? (thresholds?.linearItems || []).length
            : (thresholds?.areaItems || []).length;
        const rollingOn = visualization.configuration?.rollingAverage?.show;
        const itemCount = 4 + thresholdCount + (rollingOn ? 1 : 0);
        const rows = Math.ceil(itemCount / 3);
        return `${rows * 10}px`;
      }
      if (
        ['duval', 'duvalPentagon'].includes(vizType) &&
        visualization.configuration?.legend?.show
      ) {
        const plots = visualization.configuration?.plots || [];
        const rollingOn = visualization.configuration?.rollingAverage?.show;
        const itemCount = plots.length * (rollingOn ? 2 : 1);
        const rows = Math.ceil(itemCount / 3);
        return `${rows * 20}px`;
      }
      return '';
    }, [vizType, visualization.configuration]);

    const previewSignalCountInfoText = useMemo(() => {
      const currentVisualizationType = visualizationTypes.find(({ type }) => type === vizType);
      const vizName =
        `${currentVisualizationType?.name ?? ''} ${currentVisualizationType?.subname ?? ''}`.trim();

      if (vizType === 'star') {
        return (
          <StarSignalCountMessage
            visualizationName={vizName}
            boldClassName="signal-bold"
          />
        );
      }

      const { minSignal, maxSignal } = currentSignalCounts;
      if (minSignal === maxSignal) {
        return (
          <>
            <span className="signal-bold">{vizName}</span> visualizations require{' '}
            <span className="signal-bold">
              {minSignal} signal{minSignal === 1 ? '' : 's'}
            </span>
          </>
        );
      }

      return (
        <>
          <span className="signal-bold">{vizName}</span> visualizations require between{' '}
          <span className="signal-bold">{minSignal}</span> and{' '}
          <span className="signal-bold">{maxSignal} signals</span>
        </>
      );
    }, [vizType, currentSignalCounts]);

    useEffect(() => {
      if (sortedSiteVariables.length > 0 && variableSources.length > 0) {
        const effectiveSignalCounts = { ...currentSignalCounts, minSignal: requiredMinSignal };
        const variableSchema_ = getVariableSchema({
          sortedSiteVars: sortedSiteVariables,
          units: getValidUnitsForType(vizType),
          variableSources,
          DEFAULT_AGGREGATION_OPTIONS,
          signalCounts: effectiveSignalCounts,
        });

        if (LAST_VALUE_VISUALIZATIONS.has(vizType)) {
          delete variableSchema_.items.dependencies;
          // Only bar-gauge shows per-signal Unit/Custom Unit Label here; metric/gauge/pie/
          // grouped-bar use one unit for the whole visualization instead, and state/status don't
          // use units at all. (graph/star/duval/duvalPentagon also do per-signal units, but skip
          // this block since they aren't in LAST_VALUE_VISUALIZATIONS.)
          if (vizType !== 'bar-gauge') {
            delete variableSchema_.items.properties.unit;
            delete variableSchema_.items.properties.unitLabel;
          }
          if (vizType === 'grouped-bar') {
            delete variableSchema_.items.properties.label;
          }
          delete variableSchema_.items.properties.decimals;
        }
        setVariableSchema(() => variableSchema_);
      } else if (sourcesLoaded) {
        setVariableSchema(() => {});
      }
    }, [
      currentSignalCounts,
      requiredMinSignal,
      sourcesLoaded,
      sortedSiteVariables,
      variableSources,
      vizType,
      visualization.configuration?.inputMode,
    ]);

    const onConfigurationFormChange = useCallback(
      (data) => {
        let { formData: configuration } = data;

        // In cartesian mode, rolling average is not applicable. Force it off so the
        // stored state matches the disabled UI (unchecked + greyed out).
        if (
          vizType === 'star' &&
          configuration?.inputMode === 'cartesian' &&
          configuration?.rollingAverage?.show
        ) {
          configuration = {
            ...configuration,
            rollingAverage: { ...configuration.rollingAverage, show: false },
          };
        }

        dispatch(
          upsertVisualization(siteId, {
            id: visualization.id,
            configuration,
          })
        );

        if (!LAST_VALUE_VISUALIZATIONS.has(visualization?.type)) {
          dispatch(getSingleVisualization({ ...visualization, configuration }, panelId));
        }
      },
      [siteId, visualization, vizType, dispatch]
    );

    const componentRelationsSchema = useMemo(
      () => ({
        type: 'array',
        title: 'Relations',
        isMulti: true,
        items: {
          type: 'object',
          properties: {
            entity_id: {
              type: 'string',
              label: 'Component',
              placeholder: 'Select a component',
              components: sortedComponents,
            },
          },
        },
      }),
      [sortedComponents]
    );

    const visibleRelations = useMemo(() => {
      const scopedIds = new Set(sortedComponents.map((c) => c.id));
      return (visualization.relations || []).filter(
        (r) => r.entity_id && scopedIds.has(r.entity_id)
      );
    }, [visualization.relations, sortedComponents]);

    const componentRelationsUiSchema = {
      items: {
        entity_id: {
          'ui:field': 'componentPickerField',
        },
      },
    };

    const variableUiSchema = {
      'ui:ArrayFieldTemplate': MinItemsArrayFieldTemplate,
      'ui:ArrayFieldItemTemplate': MinItemsArrayFieldItemTemplate,
      items: {
        label: ['metric', 'health', 'gauge'].includes(visualization.type)
          ? { 'ui:classNames': 'hidden' }
          : {},
      },
    };

    const onTypeChange = (visualizationType) => {
      let configuration =
        visualizationType === 'bar-gauge' && enableSignalList
          ? { ...visualization.configuration, singleSignal: true, singleSignalId: selectedSignal }
          : visualization.configuration;
      const unitsAllowedForType = getValidUnitsForType(visualizationType);
      const unitIdsAllowedForType = new Set(unitsAllowedForType.map(({ id }) => id));

      if (configuration?.unit && !unitIdsAllowedForType.has(configuration.unit)) {
        configuration = { ...configuration, unit: DEFAULT_VISUALIZATION_UNIT_ID };
      }

      if (configuration?.axes) {
        configuration = {
          ...configuration,
          axes: normalizeVisualizationAxisUnits(configuration.axes, unitIdsAllowedForType),
        };
      }

      const normalizedVariables = normalizeVisualizationVariableUnits(
        visualization.variables,
        unitIdsAllowedForType
      );

      dispatch(
        upsertVisualization(siteId, {
          id: visualization.id,
          type: visualizationType,
          configuration,
          ...(normalizedVariables ? { variables: normalizedVariables } : {}),
        })
      );
    };

    const doSave = useCallback(() => {
      const { relations, ...viz } = visualization;
      const { maxSignal } = currentSignalCounts;
      if (visualization.variables.length > maxSignal) {
        const prioritySignalIds = getPrioritySignalIds(visualization);
        const prioritizedVariables = prioritySignalIds
          .map((signalId) => visualization.variables.find((item) => item.id === signalId))
          .filter(Boolean);
        const remainingVariables = visualization.variables.filter(
          (variable) => !prioritySignalIds.includes(variable.id)
        );

        viz.variables = [...prioritizedVariables, ...remainingVariables].slice(0, maxSignal);
      }
      dispatch(
        saveVisualization(
          {
            ...viz,
            tags: vizTags,
            isTemplate,
            relations: relations.filter((r) => r.entity_id),
          },
          parentPanelId
        )
      );
      onClose();
    }, [dispatch, onClose, visualization, currentSignalCounts, vizTags, isTemplate, parentPanelId]);

    const handleSaveVisualization = useCallback(() => {
      const { maxSignal } = currentSignalCounts;
      if (visualization.variables.length > maxSignal) {
        setShowDeleteDialog(true);
      } else {
        doSave();
      }
    }, [visualization, currentSignalCounts, doSave]);

    const prioritizedSignalsLabel = visualizationTypes.find(
      ({ type }) => type === visualization.type
    )?.prioritizedSignalsLabel;

    const availableVisualizationTypes = config.onPremise
      ? visualizationTypes.filter(({ type }) => type !== 'status')
      : visualizationTypes;

    const visualizationTypeSelectors = availableVisualizationTypes.map(
      ({ type, icon, name, subname }) => (
        <div
          key={type}
          className={`visualization-type ${type === visualization.type ? 'selected' : ''}`}
          onClick={() => onTypeChange(type)}
        >
          <Icon
            icon={icon}
            size="m"
          />
          <div className="type">{name}</div>
          {subname && (
            <div
              className="subname"
              style={{ height: 0 }}
            >
              {`(${subname})`}
            </div>
          )}
        </div>
      )
    );

    let variablePanel = (
      <div className="visualization-config--loading">
        <Spinner />
      </div>
    );

    if (sourcesLoaded) {
      variablePanel =
        Object.keys(variableSchema || {}).length > 0 ? (
          <JsonEditor
            showButtons={false}
            showEditButton={false}
            initialEditMode={true}
            formData={visualization.variables}
            schema={variableSchema}
            uiSchema={variableUiSchema}
            cancelCallback={onClose}
            onFormChange={onVariableForm}
            onFormSubmit={onVariableForm}
          />
        ) : (
          <div className="visualization-config--no-variables">
            {'No signals found.\nYou may need to add a source or import signals.'}
          </div>
        );
    }

    const previewSelectedSignal = useMemo(() => {
      if (visualization.type !== 'bar-gauge' || !enableSignalList || !selectedSignal) {
        return visualization;
      }
      return {
        ...visualization,
        variables: visualization.variables.filter((signal) => signal.id === selectedSignal),
      };
    }, [visualization, enableSignalList, selectedSignal]);

    const singleSignalOptions = useMemo(() => {
      return visualization.variables
        .filter((signal) => {
          const siteVariable = siteVariablesById[signal.id];
          return siteVariable?.dataType?.toLowerCase() === 'string';
        })
        .map((signal) => {
          const siteVariable = siteVariablesById[signal.id];
          const sourceName = siteVariable?.source?.name || '-';
          const itemDesignation =
            siteVariable?.itemDesignation || siteVariable?.importIdentifier || '-';
          const variableName = siteVariable?.name;
          return {
            value: signal.id,
            label: `[${sourceName}] [${itemDesignation}] ${variableName || signal.id}`,
          };
        });
    }, [visualization, siteVariablesById]);

    const shouldWarnNonNumeric = useMemo(() => {
      return visualization.variables.some((signal) => {
        const variable = siteVariablesById[signal.id];
        const isBoolean = variable?.dataType?.toLowerCase() === 'boolean';
        const supportsBoolean = LAST_VALUE_VISUALIZATIONS.has(visualization.type);
        const isString = variable?.dataType?.toLowerCase() === 'string';
        const supportsString = VISUALIZATIONS_SUPPORTING_STRINGS.has(visualization.type);
        const isNonNumeric = variable && !NUMERIC_DATA_TYPES.has(variable.dataType?.toLowerCase());
        return isNonNumeric && !(isBoolean && supportsBoolean) && !(isString && supportsString);
      });
    }, [visualization, siteVariablesById]);

    const normalizedConfiguration = useMemo(
      () =>
        normalizeVisualizationConfigurationColors(visualization.type, visualization.configuration),
      [visualization.type, visualization.configuration]
    );

    const disableSave =
      visualization.type === 'bar-gauge' &&
      enableSignalList &&
      Boolean(previewSingleSignalTypeError);

    const toggleSingleSignalList = useCallback(() => {
      const isSingleSignal = !enableSignalList;
      setEnableSignalList(isSingleSignal);

      let defaultSignalId = null;
      if (isSingleSignal) {
        const hasValidSelectedSignal = singleSignalOptions.some((o) => o.value === selectedSignal);
        defaultSignalId = hasValidSelectedSignal
          ? selectedSignal
          : (singleSignalOptions[0]?.value ?? null);
      }
      setSelectedSignal(defaultSignalId);

      const updatedConfiguration = {
        ...visualization.configuration,
        singleSignal: isSingleSignal,
        singleSignalId: defaultSignalId,
      };
      dispatch(
        upsertVisualization(siteId, {
          id: visualization.id,
          configuration: updatedConfiguration,
        })
      );
    }, [enableSignalList, singleSignalOptions, selectedSignal, visualization, siteId, dispatch]);

    return (
      <BigModal onClose={onClose}>
        <div className="visualizationeditor-component">
          <div className="visualization-header">
            <div className="visualization-name-header">
              <div className="name">
                <InlineEditor
                  value={visualization.name}
                  onSave={(value) =>
                    dispatch(
                      upsertVisualization(siteId, {
                        id: visualization.id,
                        name: value,
                      })
                    )
                  }
                  uniqueItems={visualizationNames}
                  uniqueError="Visualization name should be unique."
                  requiredError="Visualization name is required."
                  initialEditMode={visualization.id?.includes('temporary')}
                />
              </div>
              <TagSelect
                onChange={(selected) => setVizTags(selected)}
                value={vizTags}
                popout
                creatable={canCreateTags}
                availableTags={tags}
                menuMinWidth={15}
                category="visualizations"
              />
            </div>
            <Button
              design="text"
              onClick={onClose}
              icon={
                <Icon
                  icon="he-close"
                  size="xs"
                />
              }
            />
          </div>

          <div className="visualization-preview">
            <Visualization
              visualization={previewSelectedSignal}
              isPreview={true}
              panelId={panelId}
              pollingDateRange={pollingDateRange}
              onSingleSignalTypeErrorChange={setPreviewSingleSignalTypeError}
            />
          </div>

          <div className="visualization-config">
            {(['star', 'duval', 'duvalPentagon'].includes(visualization.type)
              ? visualization.variables.length >= requiredMinSignal
              : true) && (
              <div
                className="visualization-preview-signal-info"
                style={{ marginTop: previewInfoMarginTop }}
              >
                <Icon
                  icon="he-info"
                  size="s"
                />
                <span>{previewSignalCountInfoText}</span>
              </div>
            )}

            {shouldWarnNonNumeric && (
              <div
                className="visualization-preview-signal-info"
                style={{ marginTop: previewInfoMarginTop }}
              >
                <Icon
                  icon="he-info"
                  size="s"
                />
                <span>
                  Non-numeric signals detected. Signals with non-numeric data types are not
                  supported by this visualization type.
                </span>
              </div>
            )}
            <Tabs
              actions={
                <div className="reset-save">
                  <Button
                    activity="secondary"
                    type="text"
                    onClick={onClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    activity="primary"
                    onClick={handleSaveVisualization}
                    disabled={!valid || disableSave}
                  >
                    Save
                  </Button>
                </div>
              }
            >
              <Tab
                id="variables"
                title="Signals"
              >
                <div className="wrapper custom-scrollbar">{variablePanel}</div>
              </Tab>

              <Tab
                id="config"
                title="Visualization"
              >
                <div className="wrapper custom-scrollbar">
                  <div className="visualization-type-selector-component">
                    {visualizationTypeSelectors}
                  </div>
                  <div>
                    <Heading contentLeft={<div className="title">Use as template</div>} />
                    <Icon
                      className={`is-template-checkbox ${isTemplate ? 'on' : ''}`}
                      icon={isTemplate ? 'toggle-on' : 'toggle-off'}
                      onClick={() => setIsTemplate(!isTemplate)}
                      size="l"
                    />
                  </div>
                  {visualization.type === 'bar-gauge' && (
                    <div>
                      <Heading
                        contentLeft={<div className="title">Single signal configuration</div>}
                      />
                      <Icon
                        className={`is-template-checkbox ${enableSignalList ? 'on' : ''}`}
                        icon={enableSignalList ? 'toggle-on' : 'toggle-off'}
                        onClick={toggleSingleSignalList}
                        size="l"
                      />
                      <p className="signal-select-description">
                        Use this option to generate bars based on data from a signal which returns
                        multiple values.
                      </p>

                      <CustomSelect
                        isMulti={false}
                        isDisabled={!enableSignalList}
                        placeholder="Select Signal"
                        rawOptions={singleSignalOptions}
                        value={selectedSignal}
                        onChange={(value) => {
                          setSelectedSignal(value);
                          const updatedConfiguration = {
                            ...visualization.configuration,
                            singleSignalId: value,
                          };
                          dispatch(
                            upsertVisualization(siteId, {
                              id: visualization.id,
                              configuration: updatedConfiguration,
                            })
                          );
                        }}
                      />
                    </div>
                  )}
                  <JsonEditor
                    title="Configuration"
                    scrollIntoView={false}
                    context={visualization}
                    initialEditMode={true}
                    showEditButton={false}
                    showButtons={false}
                    schema={updatedSchema}
                    uiSchema={updatedUiSchema}
                    formData={normalizedConfiguration}
                    cancelCallback={onClose}
                    onFormChange={onConfigurationFormChange}
                  />
                </div>
              </Tab>

              <Tab
                id="relations"
                title="Relations"
              >
                <div className="wrapper custom-scrollbar">
                  <JsonEditor
                    initialEditMode={true}
                    showEditButton={false}
                    showButtons={false}
                    schema={componentRelationsSchema}
                    uiSchema={componentRelationsUiSchema}
                    cancelCallback={onClose}
                    formData={visibleRelations}
                    onFormChange={onComponentForm}
                    onFormSubmit={(data) => onComponentForm(data, true)}
                  />
                </div>
              </Tab>
            </Tabs>
            {showDeleteDialog && (
              <ConfirmationDialog
                onCancel={() => setShowDeleteDialog(false)}
                onConfirm={() => {
                  setShowDeleteDialog(false);
                  doSave();
                }}
                showCancel={true}
                title="Signals will be removed on save"
                body={
                  <>
                    <p>
                      The selected visualization type supports fewer signals than currently
                      configured.
                      <br />
                      <br />
                      {prioritizedSignalsLabel && (
                        <>
                          {`Signals that were manually added to ${prioritizedSignalsLabel} will be prioritized and kept.`}
                          <br />
                          <br />
                        </>
                      )}
                      Additional signals will be{' '}
                      <span style={{ fontWeight: 700 }}>permanently removed</span> on save.
                    </p>
                    <p style={{ paddingTop: '1.5rem' }}>
                      Are you sure you want to save the visualization and remove additional
                      signals?{' '}
                    </p>
                  </>
                }
                confirmText="Save & remove signals"
              />
            )}
          </div>
        </div>
      </BigModal>
    );
  }
);

export default VisualizationEditor;
